import { useService } from '@aesop-fables/containr-react';
import { useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { Typography, Grid } from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Column, Spacer, StepsHeader, ButtonFooter } from '../../components';
import { useLoading } from '../../hooks/useLoading';
import MembersOffboardingHeader from './MembersOffboardingHeader';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { OrgSignUpStateData } from '../../models/SignUpData';
import { UpdateSignUpState } from '../../data/signUp/mutations';
import { OffboardingWizard, offboardingWizardKey } from '../../services/offboarding';
import { MembersDataTable } from './MembersDataTable';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';

const InviteMembers = () => {
  const appStorage = useAppStorage();
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoading } = useLoading();

  const offboardingWizard = useService<OffboardingWizard>(offboardingWizardKey);
  const updateSignUpState = useMutation(new UpdateSignUpState());
  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);

  const [addMemberDrawer, setAddMemberDrawer] = useState(false);
  const { offboardingComplete } = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  ) ?? { offboardingComplete: false };

  const current = location.pathname;
  const { activeStep, totalSteps } = offboardingWizard.getStepInfo(current);

  const navBack = () => {
    navigate(-1);
  };

  const onDone = async () => {
    setLoading(true);
    const next = offboardingWizard.nextStep(location);

    if (activeStep === totalSteps && !offboardingComplete) {
      offboardingWizard.offboardingCompleted();
      await organizationDataCache.reload('organization');
    }

    await updateSignUpState.action({
      lastCompletedStep: current,
      offboardingComplete: activeStep === totalSteps,
    });

    navigate(next.route);
    setLoading(false);
  };

  const onBulkAdd = () => navigate('/members/add');

  return (
    <Column
      style={{
        padding: '0px 100px 150px 100px',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Spacer height='xs' />
      <StepsHeader activeStep={activeStep} totalSteps={totalSteps} />
      <Spacer height='xs' />

      <Grid style={{ flexGrow: 1 }}>
        <Typography variant='p50Light' color='secondary'>
          Invite members
        </Typography>
        <Spacer height='xs' />

        <MembersOffboardingHeader onBulkAdd={onBulkAdd} setAddMemberDrawer={setAddMemberDrawer} />
        <Spacer height='xs' />

        <MembersDataTable
          addMemberDrawerOpen={addMemberDrawer}
          setAddMemberDrawerOpen={setAddMemberDrawer}
        />
      </Grid>

      <ButtonFooter
        primaryButton={{
          label: activeStep === totalSteps ? `Done` : `Next`,
          className: 'next',
          onClick: onDone,
        }}
        secondaryButton={{
          label: 'Back',
          onClick: navBack,
          showCondition: true,
        }}
      />
    </Column>
  );
};

export default InviteMembers;
